import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { apiService, ConnectorConfig, Project } from '../../services/api';
import { useProjectContext } from '../../context/ProjectContext';
import { useAuth } from '../../context/AuthContext';
import { CatalogConnector } from '../../types/connectorCatalog';
import ConnectorCard from './ConnectorCard';

const ALL_PROJECTS = '__all__';
const SHARED_ONLY = '__shared__';

interface MyConnectorsTabProps {
  catalog: CatalogConnector[];
  onEditConnector: (catalogEntry: CatalogConnector, connector: ConnectorConfig) => void;
}

const MyConnectorsTab: React.FC<MyConnectorsTabProps> = ({ catalog, onEditConnector }) => {
  const theme = useTheme();
  const { projects: activeProjects, selectedProjectId } = useProjectContext();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;

  const [projectFilter, setProjectFilter] = useState<string>(ALL_PROJECTS);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch ALL connectors for the current tenant (including shared with tenant_id=NULL)
  const connectionsQuery = useQuery<ConnectorConfig[]>(
    ['connectors', 'my-connectors', tenantId],
    () => apiService.listConnectors({ scope: 'all', tenant_id: tenantId, limit: 500 }),
    { staleTime: 2 * 60 * 1000, enabled: !!tenantId }
  );

  // Fetch ALL projects (including inactive) for name resolution
  const allProjectsQuery = useQuery<Project[]>(
    ['projects', 'all-for-connectors'],
    () => apiService.listProjects({ limit: 500 }),
    { staleTime: 5 * 60 * 1000 }
  );

  const allConnectors = connectionsQuery.data || [];
  const allProjects = allProjectsQuery.data || [];

  // Build project name lookup from all projects
  const projectNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of allProjects) {
      map[p.project_id] = p.name;
    }
    return map;
  }, [allProjects]);

  // Build list of projects that actually have connectors (for the dropdown)
  const projectsWithConnectors = useMemo(() => {
    const projectIds = new Set(allConnectors.map((c) => c.project_id).filter(Boolean));
    return allProjects.filter((p) => projectIds.has(p.project_id));
  }, [allConnectors, allProjects]);

  // Build catalog lookup: connector_id -> CatalogConnector
  const catalogByType = useMemo(() => {
    const map: Record<string, CatalogConnector> = {};
    for (const c of catalog) {
      map[c.connector_id] = c;
    }
    return map;
  }, [catalog]);

  // Resolve catalog entry from a ConnectorConfig
  const getCatalogEntry = (conn: ConnectorConfig): CatalogConnector | null => {
    const match = conn.connector_type.match(/^dlt_(.+)_(source|destination)$/);
    const catalogId = match?.[1];
    return catalogId ? catalogByType[catalogId] ?? null : null;
  };

  // Filter connectors by project selection and search
  const filteredConnectors = useMemo(() => {
    let items = allConnectors;

    // Filter by project
    if (projectFilter === SHARED_ONLY) {
      items = items.filter((c) => !c.project_id);
    } else if (projectFilter !== ALL_PROJECTS) {
      items = items.filter((c) => c.project_id === projectFilter);
    }

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((c) => {
        const nameMatch = c.name.toLowerCase().includes(q);
        const catalogEntry = getCatalogEntry(c);
        const catalogMatch = catalogEntry
          ? catalogEntry.display_name.toLowerCase().includes(q) ||
            catalogEntry.short_description.toLowerCase().includes(q)
          : false;
        return nameMatch || catalogMatch;
      });
    }

    return items;
  }, [allConnectors, projectFilter, searchQuery, catalogByType]);

  // Group connectors by project for "All Projects" view
  const groupedConnectors = useMemo(() => {
    if (projectFilter !== ALL_PROJECTS) return null;

    const groups: Record<string, ConnectorConfig[]> = {};
    for (const conn of filteredConnectors) {
      const key = conn.project_id || SHARED_ONLY;
      if (!groups[key]) groups[key] = [];
      groups[key].push(conn);
    }

    // Sort: current project first, then other projects alphabetically, then shared last
    const entries = Object.entries(groups);
    entries.sort(([a], [b]) => {
      if (a === selectedProjectId) return -1;
      if (b === selectedProjectId) return 1;
      if (a === SHARED_ONLY) return 1;
      if (b === SHARED_ONLY) return -1;
      const nameA = projectNameMap[a] || a;
      const nameB = projectNameMap[b] || b;
      return nameA.localeCompare(nameB);
    });

    return entries;
  }, [filteredConnectors, projectFilter, selectedProjectId, projectNameMap]);

  const renderConnectorCard = (conn: ConnectorConfig) => {
    const catalogEntry = getCatalogEntry(conn);
    if (!catalogEntry) return null;

    return (
      <Grid item xs={6} sm={4} md={3} lg={2} key={conn.connector_id}>
        <ConnectorCard
          connector={catalogEntry}
          isConnected
          connectionName={conn.name}
          isGlobal={!conn.project_id}
          projectName={conn.project_id ? projectNameMap[conn.project_id] : undefined}
          onClick={() => onEditConnector(catalogEntry, conn)}
        />
      </Grid>
    );
  };

  if (connectionsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasShared = allConnectors.some((c) => !c.project_id);

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={projectFilter}
            label="Project"
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <MenuItem value={ALL_PROJECTS}>All Projects</MenuItem>
            {hasShared && <MenuItem value={SHARED_ONLY}>Shared (No Project)</MenuItem>}
            {projectsWithConnectors.map((p) => (
              <MenuItem key={p.project_id} value={p.project_id}>
                {p.name}
                {p.project_id === selectedProjectId && (
                  <Chip label="current" size="small" sx={{ ml: 1, fontSize: '0.6rem', height: 18 }} color="primary" variant="outlined" />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search connectors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />

        <Chip
          label={`${allConnectors.length} connector${allConnectors.length !== 1 ? 's' : ''}`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      </Stack>

      {/* No connectors state */}
      {allConnectors.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No connectors configured yet. Switch to the Catalog tab to add one.
          </Typography>
        </Box>
      )}

      {/* No results for filter */}
      {allConnectors.length > 0 && filteredConnectors.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No connectors match your filters.
          </Typography>
        </Box>
      )}

      {/* Grouped view (All Projects) */}
      {groupedConnectors && groupedConnectors.map(([groupKey, connectors]) => {
        const groupLabel =
          groupKey === SHARED_ONLY
            ? 'Shared (No Project)'
            : projectNameMap[groupKey] || 'Unknown Project';
        const isCurrent = groupKey === selectedProjectId;

        return (
          <Fade in timeout={400} key={groupKey}>
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {groupLabel}
                </Typography>
                {isCurrent && (
                  <Chip label="current project" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                )}
                <Chip
                  label={connectors.length}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: theme.palette.action.hover }}
                />
              </Stack>
              <Grid container spacing={2}>
                {connectors.map(renderConnectorCard)}
              </Grid>
            </Box>
          </Fade>
        );
      })}

      {/* Flat view (specific project or shared) */}
      {!groupedConnectors && filteredConnectors.length > 0 && (
        <Fade in timeout={400}>
          <Grid container spacing={2}>
            {filteredConnectors.map(renderConnectorCard)}
          </Grid>
        </Fade>
      )}
    </Box>
  );
};

export default MyConnectorsTab;
